import React, { FC, useMemo, useState } from 'react'
import { Avatar, ListItemText, Paper, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material'
import { HiChevronDoubleRight } from 'react-icons/hi2'
import { arrayBufferToBase64 } from '@plug-core/utils/arrayBuffer'
import TabPanel from '@components/TabPanel'
import { shortAddress } from '@plug-core/utils/shortAddress'
import { getPrettyDecimals } from '@plug-core/utils/validationHelper'
import useConfig from '@plug-core/hooks/useConfig'
import useToken from '@plug-core/hooks/useToken'
import { formatMoney } from '@plug-core/utils/formatNumber'

import type { RequestTransferParams } from '@plug-core/interfaces/transfers'

interface Props {
  transferParams: RequestTransferParams
  tokenCanisterId: string
}

const Transfer: FC<Props> = ({ transferParams, tokenCanisterId }) => {
  const [currentTab, setCurrentTab] = useState(0)

  const { tokenPricesWithChange } = useConfig()
  const { palette } = useTheme()
  const { id, decimals, logo, symbol, fee } = useToken(tokenCanisterId)

  const tokenPrice = id ? tokenPricesWithChange[id] : undefined

  let usdAmount
  if (typeof tokenPrice?.price === 'number') {
    usdAmount = transferParams.amount * 10 ** -(decimals || 8) * tokenPrice.price
  }

  const renderArrowView = () => {
    return (
      <Stack
        position="absolute"
        justifyContent="center"
        p={1}
        bgcolor={(theme) => theme.palette.background.default}
        borderRadius={10}
      >
        <HiChevronDoubleRight color={palette.primary.main} size={30} />
      </Stack>
    )
  }

  const renderPropertyRow = (title: string, value: string | number) => (
    <Stack spacing={1}>
      <Typography variant="body2">{title}</Typography>
      <Stack
        bgcolor={(theme) => theme.palette.background.paper}
        sx={{ borderRadius: 1 }}
        px={2}
        py={1}
        minHeight={20}
      >
        <Typography sx={{ overflowWrap: 'break-word' }}>{value}</Typography>
      </Stack>
    </Stack>
  )

  return (
    <Stack flex={1}>
      <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
        <Tab sx={{ flex: 1 }} label="Info" />
        <Tab sx={{ flex: 1 }} label="Parameters" />
      </Tabs>
      <TabPanel value={currentTab} index={0}>
        <Stack flex={1} justifyContent="space-between">
          <Stack direction="row" justifyContent="center" alignItems="center" mb={3.5}>
            <Stack component={Paper} alignItems="center" spacing={1.5} p={2} ml={1} flex={1}>
              <Avatar src={logo} />
              <Stack spacing={0.5}>
                <Typography variant="caption" textAlign="center">
                  {symbol}
                </Typography>
              </Stack>
            </Stack>
            {renderArrowView()}
            <Stack flex={1} component={Paper} alignItems="center" spacing={1.5} p={2} mx={1}>
              <Avatar>😇</Avatar>
              <Stack spacing={0.5}>
                <Typography variant="caption" textAlign="center">
                  {shortAddress(transferParams.to)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Stack px={2} spacing={0.5}>
            <Stack direction="row">
              <ListItemText primary="Transfer amount" />
              <ListItemText
                primaryTypographyProps={{
                  textAlign: 'right',
                  color: (theme) => theme.palette.rainbow.blue,
                  fontSize: 23,
                }}
                secondaryTypographyProps={{
                  textAlign: 'right',
                  marginTop: 0.5,
                }}
                primary={`${getPrettyDecimals(transferParams.amount, decimals, 4)} ${symbol}`}
                secondary={formatMoney(usdAmount)}
              />
            </Stack>
            <Stack direction="row">
              <ListItemText primary="Fee" />
              <ListItemText
                secondary={`${getPrettyDecimals(fee || 0, decimals || 8)} ${symbol}`}
                secondaryTypographyProps={{
                  textAlign: 'right',
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </TabPanel>
      <TabPanel value={currentTab} index={1}>
        <Stack px={2} spacing={1}>
          {renderPropertyRow('Token Canister ID', tokenCanisterId)}
          {renderPropertyRow('Receiver', transferParams.to)}
          {transferParams.opts?.memo && renderPropertyRow('Memo', transferParams.opts?.memo)}
          {transferParams.subaccount &&
            renderPropertyRow('Subaccount', arrayBufferToBase64(transferParams.subaccount))}
        </Stack>
      </TabPanel>
    </Stack>
  )
}

export default Transfer
